import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { getFirestore, query, where, getDocs, orderBy, doc, deleteDoc, limit, addDoc, collection, serverTimestamp, getDoc } from 'firebase/firestore';
// Assuming logCustomEvent is a utility that might be used. If it's not passed, it would need to be imported or handled.
// import { logCustomEvent } from '../components/UtilAnalytics'; // Adjust path as needed

// Helper function for input validation (moved from CompAIStoryBoardv2)
const validateFormFields = (formDataToCheck) => {
    const requiredFields = [
        'promptCompanyName',
        'promptProductName',
        'promptCustomerRole',
        'promptCustomerName',
        'promptCustomerProblem'
    ];

    const emptyFields = requiredFields.filter(field => !formDataToCheck[field]);
    return emptyFields;
};

// Helper function for API calls with retry (moved from generateStory)
const callAPIWithRetry = async (apiCall, params, maxRetries = 2, retryDelay = 3000) => {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await apiCall(params);
        } catch (error) {
            lastError = error;
            if (attempt < maxRetries) {
                //console.log(`Retrying... Attempt ${attempt + 1} of ${maxRetries}`);
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }
    }
    throw lastError;
};

export const executeGenerateStory = async (params) => {
    const {
        // State values
        formData, storyTone, user, apiEndpoint, groundingDocs,
        // State setters
        setIsFormSubmitted, setError, setTimer, setIsTimerRunning, setLoading, setShowProgressBar,
        setApiCalls, setCurrentStep, setApiCallsCompleted, setSectionTimes,
        setResearchData, setResearchDeepData, setBusinessModelData, setBusinessNewsData,
        setIndustryTrendsData, setEli5DataBusiness, setEli5DataIndustry, setMaxDeepData,
        setPersonaData, setStoryData, setProducedResult, setProducedStory,
        setStoryVisualizationData, setStoryChaptersData, setBusinessAnalysisData,
        setEli5BusinessValueData, setStorySpeakerData, setStorySpeakerSummaryResult,
        setBusinessTransformationResult, setBusinessCostOfInactionResult,
        setSalesAnalysisData, setModalData, setIntelligenceFoundation, setOpenPaths,
        setRecentPrompts, setCaseStudiesData, setCompetitiveAnalysisResult,  
        // Helper functions from component
        handleOpen, playSound,
        // Firebase instances
        functions, db,
        // Utilities
        logCustomEvent
    } = params;

    setIsFormSubmitted(true);
    logCustomEvent('gen_story', {});

    const emptyFields = validateFormFields(formData);
    if (emptyFields.length > 0) {
        setError('Please fill in all required fields');
        setLoading(false);
        setShowProgressBar(false);
        setIsTimerRunning(false);
        return;
    }

    setError(null);
    setTimer(0);
    setIsTimerRunning(true);
    setLoading(true);
    setShowProgressBar(true);

    let producedResultLocal;
    let storyResultLocal;
    let businessAnalysisDataFeeder;
    let salesAnalysisDataFeeder;
    let storySpeakerSummaryFeeder;
    let storyChaptersDataFeeder;
    let businessTransformationFeeder;
    let businessCostOfInactionFeeder;
    let personaDataFeeder;
    let researchDataFeeder;
    let businessNewsFeeder;
    let industryTrendsFeeder;
    let businessModelDataFeeder;
    let maxDeepDataFeeder;
    let eli5DataBusinessFeeder;
    let eli5DataIndustryFeeder;
    let researchDeepDataFeeder;
    let eli5BusinessValueDataLocal;
    let caseStudiesDataFeeder;
    let competitiveAnalysisResultFeeder;

    // Promises for background tasks
    let competitiveAnalysisPromise;
    let caseStudiesPromise;

    try {
        try {
            const promptCacheRef = collection(db, "gen-prompt-cache");
            const q = query(promptCacheRef,
                where("promptCompanyName", "==", formData.promptCompanyName),
                where("promptProductName", "==", formData.promptProductName),
                where("promptCustomerRole", "==", formData.promptCustomerRole),
                where("promptCustomerName", "==", formData.promptCustomerName),
                where("promptCustomerProblem", "==", formData.promptCustomerProblem),
                where("userId", "==", user?.uid)
            );
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) {
                const currentDate = new Date();
                const ts = serverTimestamp();
                const newPromptCache = await addDoc(promptCacheRef, {
                    promptCompanyName: formData.promptCompanyName,
                    promptProductName: formData.promptProductName,
                    promptCustomerRole: formData.promptCustomerRole,
                    promptCustomerName: formData.promptCustomerName,
                    promptCustomerProblem: formData.promptCustomerProblem,
                    userId: user?.uid,
                    date: currentDate.toLocaleDateString(),
                    time: currentDate.toLocaleTimeString(),
                    timestamp: ts,
                    storyTone: {
                        storyIntensity: storyTone.storyIntensity,
                        storyLength: storyTone.storyLength,
                        story: storyTone.story,
                        research: storyTone.research,
                        storyTone: storyTone.storyTone
                    }
                });
                const newPromptDocRef = doc(db, "gen-prompt-cache", newPromptCache.id);
                const newPromptDoc = await getDoc(newPromptDocRef);
                setRecentPrompts(prev => [
                    ...prev,
                    { id: newPromptCache.id, ...newPromptDoc.data() }
                ].sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0)).slice(0,5));
            }
        } catch (error) {
            console.error("Error saving prompt cache:", error);
        }

        // Initiate competitiveAnalysisPromise as a background task
        competitiveAnalysisPromise = (async () => {
            try {
                setApiCalls(prev => ({ ...prev, competitiveAnalysis: true }));
                setCurrentStep("Starting Competitive Analysis (Background)");
                const competitiveAnalysisStartTime = Date.now();
                //console.log("[BACKGROUND] Calling generateCompetitiveAnalysis with formData:", formData);
                const competitiveAnalysisFunction = httpsCallable(functions, 'generateCompetitiveAnalysis', { timeout: 540000 });
                const competitiveAnalysisResult = await competitiveAnalysisFunction({
                    promptCompanyName: formData.promptCompanyName,
                    promptProductName: formData.promptProductName,
                    promptCustomerName: formData.promptCustomerName,
                    promptCustomerRole: formData.promptCustomerRole,
                    promptCustomerProblem: formData.promptCustomerProblem,
                });
                //console.log("[BACKGROUND] generateCompetitiveAnalysis result:", competitiveAnalysisResult);
                competitiveAnalysisResultFeeder = competitiveAnalysisResult.data;
                setCompetitiveAnalysisResult(competitiveAnalysisResult.data); // Update state
                setApiCalls(prev => ({ ...prev, competitiveAnalysis: false }));
                setSectionTimes(prev => ({
                    ...prev,
                    competitiveAnalysis: Date.now() - competitiveAnalysisStartTime
                }));
                setApiCallsCompleted(prev => ({ ...prev, competitiveAnalysis: true }));
                // playSound(); // Optional: Sound for this specific background task
                return competitiveAnalysisResult.data;
            } catch (error) {
                console.error("[BACKGROUND] Error calling generateCompetitiveAnalysis:", error);
                setApiCalls(prev => ({ ...prev, competitiveAnalysis: false }));
                // Consider how to handle this error, e.g., setError or log
                // setError(`Competitive Analysis failed: ${error.message}`);
                return null; // Graceful failure for background task
            }
        })();

        // Initiate caseStudiesPromise as a background task
        caseStudiesPromise = (async () => {
            //console.log("Stage 1 - Case Study Gathering (Background): Starting...");
            try {
                setApiCalls(prev => ({ ...prev, caseStudies: true }));
                setCurrentStep("Fetching Case Studies (Background)");
                const caseStudiesStartTime = Date.now();
                const caseStudiesCallable = httpsCallable(functions, 'orchestrateCaseStudyGathering', { timeout: 540000 });
                const result = await callAPIWithRetry(p => caseStudiesCallable(p), formData);
                caseStudiesDataFeeder = result.data;
                //console.log("Case Studies Feeder (Background):", caseStudiesDataFeeder);
                setCaseStudiesData(caseStudiesDataFeeder);
                setApiCalls(prev => ({ ...prev, caseStudies: false }));
                setSectionTimes(prev => ({ ...prev, caseStudies: Date.now() - caseStudiesStartTime }));
                setApiCallsCompleted(prev => ({ ...prev, caseStudies: true }));
                // playSound(); // Optional
                //console.log("Stage 1 - Case Study Gathering (Background): Completed successfully.");
                return result.data;
            } catch (e) {
                console.error("Case Study Gathering error (Background):", e);
                setError(`Case Study Gathering failed: ${e.message}`); // This error might be shown to user
                setApiCalls(prev => ({ ...prev, caseStudies: false }));
                return null; // Graceful failure for background task
            }
        })();

        // 1. Research call
        try {
            setApiCalls(prev => ({ ...prev, research: true }));
            setCurrentStep("Starting Research"); 
            const researchStartTime = Date.now();

            const researchPromise = (async () => {
                const researchResponse = await fetch(apiEndpoint + 'jobsResearcher?' + new URLSearchParams(formData));
                if (!researchResponse.ok) throw new Error(`HTTP error! status: ${researchResponse.status}`);
                const researchResult = await researchResponse.json();
                researchDataFeeder = researchResult;

                try {
                    const jobsResearchDeepFunction = httpsCallable(functions, 'jobsResearchDeepStory', { timeout: 540000 });
                    const deepResearchResponse = await jobsResearchDeepFunction({
                        ...formData, synthData: researchResult, tone: storyTone.research, version: 1, mediaProcessing: 0,
                    });
                    setResearchDeepData(deepResearchResponse.data.researchDeepData);
                    researchDeepDataFeeder = deepResearchResponse.data.researchDeepData;
                } catch (deepResearchError) {
                    console.error("Deep Research API error:", deepResearchError);
                }

                try {
                    const jobsBusinessModelFunction = httpsCallable(functions, 'jobsBusinessModel', { timeout: 540000 });
                    const businessModelResponse = await jobsBusinessModelFunction({
                        ...formData, synthData: researchResult, tone: storyTone.research, version: 1, mediaProcessing: 0,
                    });
                    const businessModelResult = businessModelResponse.data;
                    businessModelDataFeeder = businessModelResult.businessModelData;
                    setBusinessModelData(businessModelResult.businessModelData);
                } catch (businessModelError) {
                    console.error("Business Model API error:", businessModelError);
                }
                return researchResult; // Return for Promise.all
            })();

            const [researchResult] = await Promise.all([researchPromise]);

            // Post-Promise.all updates
            setResearchData(researchResult);
            // competitiveAnalysisResultFeeder and setCompetitiveAnalysisResult are handled by its own IIFE

            setApiCalls(prev => ({ ...prev, research: false }));
            setSectionTimes(prev => ({
                ...prev,
                research: Date.now() - researchStartTime,
                // competitiveAnalysis time is set within its own IIFE
            }));
            handleOpen(1); 
            playSound();
        } catch (error) {
            console.error("Research API error:", error);
            setError(`Research failed: ${error.message}`);
            setApiCalls(prev => ({ ...prev, research: false }));
            throw error;
        }

        // Parallel research processes
        try {
            setCurrentStep("Gathering Research");
            const businessNewsPromise = (async () => {

                // Case Study Gathering is now a separate background task (caseStudiesPromise)
                // The logic previously here has been moved.

                try {
                    const businessNewsStartTime = Date.now();
                    setApiCalls(prev => ({ ...prev, businessNews: true }));
                    const jobsBusinessNewsDeep = httpsCallable(functions, 'jobsBizDeep', { timeout: 540000 });
                    const businessNewsResult = await callAPIWithRetry( p => jobsBusinessNewsDeep(p), { ...formData, groundingDocs });
                    businessNewsFeeder = businessNewsResult.data;
                    setBusinessNewsData(businessNewsResult.data);
                    setApiCalls(prev => ({ ...prev, businessNews: false }));
                    setApiCallsCompleted(prev => ({ ...prev, businessNews: true }));
                    setSectionTimes(prev => ({ ...prev, businessNews: Date.now() - businessNewsStartTime }));
                    
                    const eli5StartTime = Date.now();
                    setApiCalls(prev => ({ ...prev, eli5Business: true }));
                    setCurrentStep("Summarizing Insights");
                    const jobsELI5b = httpsCallable(functions, 'jobsELI5b', { timeout: 540000 });
                    const eli5Result = await callAPIWithRetry( p => jobsELI5b(p), { ...formData, tone: storyTone.research, version: 2, synthData: businessNewsResult.data });
                    setEli5DataBusiness(eli5Result.data);
                    eli5DataBusinessFeeder = eli5Result.data;
                    setApiCalls(prev => ({ ...prev, eli5Business: false }));
                    setSectionTimes(prev => ({ ...prev, eli5Business: Date.now() - eli5StartTime }));
                    setApiCallsCompleted(prev => ({ ...prev, eli5Business: true }));
                    playSound(); handleOpen(2); return { success: true };
                } catch (e) { console.error("BN error:", e); setApiCalls(prev=>({...prev, businessNews:false, eli5Business:false})); setError(`BI failed: ${e.message}`); return {success:false, error:e}; }
            })();
            const customerLanguagePromise = (async () => {
                try {
                    const deepLanguageStartTime = Date.now();
                    setSectionTimes(prev => ({ ...prev, deepLanguage: deepLanguageStartTime }));
                    setApiCalls(prev => ({ ...prev, deepLanguage: true }));
                    setCurrentStep("Customer Language");
                    const jobsMaxDeepFunction = httpsCallable(functions, 'jobsMaxConversationDeep', { timeout: 540000 });
                    const maxDeepResponse = await callAPIWithRetry( () => jobsMaxDeepFunction({ ...formData, tone: storyTone.research, version: 1, mediaProcessing: 0 }), null );
                    setMaxDeepData(maxDeepResponse.data.researchDeepData);
                    setSectionTimes(prev => ({ ...prev, deepLanguage: Date.now() - deepLanguageStartTime }));
                    setApiCalls(prev => ({ ...prev, deepLanguage: false }));
                    setApiCallsCompleted(prev => ({ ...prev, deepLanguage: true }));
                    maxDeepDataFeeder = maxDeepResponse.data;
                    handleOpen(4); playSound(); return { success: true };
                } catch (e) { console.error("Max Deep error:", e); setApiCalls(prev=>({...prev, deepLanguage:false})); return {success:false, error:e}; }
            })();
            const industryTrendsPromise = (async () => {
                try {
                    setApiCalls(prev => ({ ...prev, industryTrends: true }));
                    setCurrentStep("Industry Trends");
                    const trendsStartTime = Date.now();
                    const jobsIndDeep = httpsCallable(functions, 'jobsIndDeep', { timeout: 540000 });
                    const trendsResult = await callAPIWithRetry( p => jobsIndDeep(p), formData );
                    industryTrendsFeeder = trendsResult.data;
                    setIndustryTrendsData(trendsResult.data);
                    setApiCalls(prev => ({ ...prev, industryTrends: false }));
                    setApiCallsCompleted(prev => ({ ...prev, industryTrends: true }));
                    setSectionTimes(prev => ({ ...prev, industryTrends: Date.now() - trendsStartTime }));
                    const eli5IndustryStartTime = Date.now();
                    setApiCalls(prev => ({ ...prev, eli5Industry: true }));
                    setCurrentStep("Summarizing Trends");
                    const jobsELI5Trends = httpsCallable(functions, 'jobsELI5b', { timeout: 540000 });
                    const eli5Result = await callAPIWithRetry( p => jobsELI5Trends(p), { ...formData, tone: storyTone.research, version: 3, synthData: trendsResult.data });
                    setEli5DataIndustry(eli5Result.data);
                    eli5DataIndustryFeeder = eli5Result.data;
                    setSectionTimes(prev => ({ ...prev, eli5Industry: Date.now() - eli5IndustryStartTime }));
                    setApiCalls(prev => ({ ...prev, eli5Industry: false }));
                    setApiCallsCompleted(prev => ({ ...prev, eli5Industry: true }));
                    handleOpen(3); playSound(); return { success: true };
                } catch (e) { console.error("IT error:", e); setApiCalls(prev=>({...prev, industryTrends:false, eli5Industry:false})); setError(`IT failed: ${e.message}`); return {success:false, error:e}; }
            })();
            const results = await Promise.all([ businessNewsPromise, customerLanguagePromise, industryTrendsPromise ]);
            const failures = results.filter(r => !r.success);
            if (failures.length > 0) throw failures[0].error;
        } catch (error) {
            console.error("Parallel research error:", error);
            setError(`Parallel research failed: ${error.message}`);
            throw error;
        }

        // 5. Persona
        try {
            setApiCalls(prev => ({ ...prev, persona: true }));
            setCurrentStep("Stakeholder Analysis");
            const personaStartTime = Date.now();
            const jobsCast = httpsCallable(functions, 'jobsCast', { timeout: 540000 });
            const personaResult = await callAPIWithRetry( p => jobsCast(p), { ...formData, podcastInsights: researchDataFeeder.podcastInsights });
            personaDataFeeder = personaResult.data;
            setPersonaData(personaResult.data);
            setApiCalls(prev => ({ ...prev, persona: false }));
            setSectionTimes(prev => ({ ...prev, persona: Date.now() - personaStartTime }));
            setApiCallsCompleted(prev => ({ ...prev, persona: true }));
            handleOpen(5); playSound();
        } catch (error) {
            console.error("Persona error:", error);
            setApiCalls(prev => ({ ...prev, persona: false }));
            setError(`Stakeholder analysis failed: ${error.message}`);
            throw error;
        }

        // 6. Story Generation
        try {
            setApiCalls(prev => ({ ...prev, story: true }));
            setCurrentStep("Thinking Story");
            const storyStartTime = Date.now();
            const jobsStoryteller = httpsCallable(functions, 'jobsStoryteller', { timeout: 540000 });
            const storyResponse = await callAPIWithRetry( p => jobsStoryteller(p), {
                customerInsights: researchDataFeeder?.customerInsights || [], productInsights: researchDataFeeder?.productInsights || [],
                podcastInsights: researchDataFeeder?.podcastInsights || [], personas: personaDataFeeder || [],
                promptCompanyName: formData?.promptCompanyName || "", promptProductName: formData?.promptProductName || "",
                promptCustomerName: formData?.promptCustomerName || "", promptCustomerRole: formData?.promptCustomerRole || "",
                promptCustomerProblem: formData?.promptCustomerProblem || "", promptInspiration: formData?.promptCustomerProblem || "",
                businessNewsResult: businessNewsFeeder || {}, industryTrendsResult: industryTrendsFeeder || {},
                storyIntensity: storyTone?.storyIntensity || "medium", storyLength: storyTone?.storyLength || "medium", tone: storyTone?.story || "neutral"
            });
            storyResultLocal = storyResponse.data;
            setStoryData(storyResultLocal);
            setApiCalls(prev => ({ ...prev, story: false }));
            setApiCallsCompleted(prev => ({ ...prev, story: true }));
            setSectionTimes(prev => ({ ...prev, story: Date.now() - storyStartTime }));
            playSound();
        } catch (error) {
            console.error("Story gen error:", error);
            setApiCalls(prev => ({ ...prev, story: false }));
            setError(`Story gen failed: ${error.message}`);
            throw error;
        }

        // 7. Producer
        try {
            setApiCalls(prev => ({ ...prev, producer: true }));
            const producerStartTime = Date.now();
            setCurrentStep("Framing Story");
            const jobsStoryProducer = httpsCallable(functions, 'jobsStoryProducer', { timeout: 540000 });
            const producerResponse = await callAPIWithRetry( p => jobsStoryProducer(p), {
                ...formData, storyIntensity: storyTone.storyIntensity, storyLength: storyTone.storyLength, story: storyResultLocal,
                story_scenes: storyResultLocal.story.scenes, story_introduction: storyResultLocal.story.introduction,
                story_conclusion: storyResultLocal.story.conclusion, story_personas: personaDataFeeder,
                story_introduction_summary: storyResultLocal.story.introduction, story_conclusion_summary: storyResultLocal.story.conclusion,
                story_conclusion_benefits: storyResultLocal.story.conclusion,
                titlePrompt: `Create a compelling title for the story of how ${formData.promptCustomerName} solved ${formData.promptCustomerProblem} using ${formData.promptProductName}`,
                openingPrompt: `Create an engaging opening for the story of how ${formData.promptCustomerName} implemented ${formData.promptProductName}`,
                closingPrompt: `Create a powerful conclusion highlighting how ${formData.promptProductName} helped ${formData.promptCustomerName} solve ${formData.promptCustomerProblem}`,
                tone: storyTone.story
            });
            producedResultLocal = producerResponse.data;
            setProducedResult(producedResultLocal);
            setProducedStory(producedResultLocal);
            setStoryVisualizationData(producedResultLocal);
            setApiCallsCompleted(prev => ({ ...prev, producer: true }));
            setApiCalls(prev => ({ ...prev, producer: false }));
            setSectionTimes(prev => ({ ...prev, producer: Date.now() - producerStartTime }));
            playSound();
        } catch (error) {
            console.error("Producer error:", error);
            setApiCalls(prev => ({ ...prev, producer: false }));
            setError(`Story framing failed: ${error.message}`);
            throw error;
        }

        // 8. Parallel post-story processes
        try {
            setApiCalls(prev => ({ ...prev, storyChapters: true, salesStrategy: true, businessAnalysis: true, salesAnalysis: true }));
            const storyChaptersStartTime = Date.now();
            const businessAnalysisStartTime = Date.now();
            const salesAnalysisStartTime = Date.now();

            const storyChaptersPromise = (async () => {
                try {
                    setCurrentStep("Generating Chapters");
                    const storyChaptersCallable = httpsCallable(functions, 'jobsStoryChapters', { timeout: 540000 });
                    const chaptersResult = await callAPIWithRetry( p => storyChaptersCallable(p), {
                        promptCompanyName: formData?.promptCompanyName || "", promptProductName: formData?.promptProductName || "",
                        promptCustomerName: formData?.promptCustomerName || "", promptCustomerRole: formData?.promptCustomerRole || "",
                        promptCustomerProblem: formData?.promptCustomerProblem || "", producedWrapper: producedResultLocal || {},
                        personaData: personaDataFeeder || [], researchData: researchDataFeeder || {},
                        executiveInterview: researchDataFeeder?.podcastInsights || [], businessNewsData: businessNewsFeeder || {},
                        industryTrendsData: industryTrendsFeeder || {}, tone: storyTone?.story || "neutral",
                        storyIntensity: storyTone?.storyIntensity || "medium", customerInsights: researchDataFeeder?.customerInsights || [],
                        productInsights: researchDataFeeder?.productInsights || [], podcastInsights: researchDataFeeder?.podcastInsights || [],
                        caseStudiesData: caseStudiesDataFeeder||{}, competitiveAnalysisResult: competitiveAnalysisResultFeeder
                    });
                    storyChaptersDataFeeder = chaptersResult.data;
                    setStoryChaptersData(storyChaptersDataFeeder);
                    setApiCalls(prev => ({ ...prev, storyChapters: false }));
                    setApiCallsCompleted(prev => ({ ...prev, storyChapters: true }));
                    setSectionTimes(prev => ({ ...prev, storyChapters: Date.now() - storyChaptersStartTime }));
                    playSound(); return storyChaptersDataFeeder;
                } catch (e) { console.error("SC error:", e); setApiCalls(prev=>({...prev, storyChapters:false})); setError(`Chapter gen failed: ${e.message}`); return null; }
            })();

            const businessAnalysisPromise = (async () => {
                try {
                    setCurrentStep("Business Analysis");
                    const jobsBusinessAnalysis = httpsCallable(functions, 'jobsBusinessValue', { timeout: 540000 });
                    const analysisResult = await callAPIWithRetry( p => jobsBusinessAnalysis(p), {
                        formData, businessNewsData: businessNewsFeeder||{}, industryTrendsData: industryTrendsFeeder||{},
                        maxDeepData: maxDeepDataFeeder||{}, researchData: researchDataFeeder||{}, customerInsights: researchDataFeeder?.customerInsights||{},
                        productInsights: researchDataFeeder?.productInsights||{}, producedResult: producedResultLocal||{}, tone: storyTone.research,
                        personaResult: personaDataFeeder||{}, executiveInterview: researchDataFeeder?.podcastInsights||{}, 
                        caseStudiesData: caseStudiesDataFeeder||{}, competitiveAnalysisResult: competitiveAnalysisResultFeeder
                    });
                    businessAnalysisDataFeeder = analysisResult.data;
                    setBusinessAnalysisData(businessAnalysisDataFeeder);
                    setApiCallsCompleted(prev => ({ ...prev, businessAnalysis: true }));
                    setApiCalls(prev => ({ ...prev, businessAnalysis: false }));
                    setSectionTimes(prev => ({ ...prev, businessAnalysis: Date.now() - businessAnalysisStartTime }));
                    playSound();
                    const jobsELI5b_value = httpsCallable(functions, 'jobsELI5b', { timeout: 540000 });
                    const eli5ResultValue = await callAPIWithRetry( p => jobsELI5b_value(p), {
                        ...formData, synthData: businessAnalysisDataFeeder, tone: storyTone.story,
                        storyIntensity: storyTone.storyIntensity, storyLength: storyTone.storyLength, version: 4,
                    });
                    eli5BusinessValueDataLocal = eli5ResultValue.data;
                    setEli5BusinessValueData(eli5BusinessValueDataLocal);
                    setApiCallsCompleted(prev => ({ ...prev, eli5BusinessValue: true }));
                    setApiCalls(prev => ({ ...prev, eli5BusinessValue: false }));
                    playSound(); return businessAnalysisDataFeeder;
                } catch (e) { console.error("BA/ELI5 error:",e); setApiCalls(prev=>({...prev,businessAnalysis:false,eli5BusinessValue:false})); setError(`BA/ELI5 failed: ${e.message}`); return null; }
            })();

            const [storyChaptersDataResult, businessAnalysisDataResult] = await Promise.all([ storyChaptersPromise, businessAnalysisPromise ]);

            if (businessAnalysisDataResult) {
                try {
                    const storySpeakerStartTime = Date.now();
                    setApiCalls(prev => ({ ...prev, storySpeaker: true }));
                    setCurrentStep("Generating Talktrack");
                    const storySpeakerCallable = httpsCallable(functions, 'jobsStorySpeaker', { timeout: 540000 });
                    const speakerResult = await callAPIWithRetry( p => storySpeakerCallable(p), {
                        formData: { ...formData, tone: storyTone.story }, storyData: storyResultLocal,
                        storyIntensity: storyTone.storyIntensity, storyLength: storyTone.storyLength,
                        tone: storyTone.story, businessAnalysis: businessAnalysisDataResult
                    });
                    setStorySpeakerData(speakerResult.data);
                    setApiCalls(prev => ({ ...prev, storySpeaker: false }));
                    setApiCallsCompleted(prev => ({ ...prev, storySpeaker: true }));
                    setSectionTimes(prev => ({ ...prev, storySpeaker: Date.now() - storySpeakerStartTime }));
                    playSound();

                    const storySpeakerSummaryStartTime = Date.now();
                    setApiCalls(prev => ({ ...prev, storySpeakerSummary: true }));
                    setCurrentStep("Generating Summary");
                    const jobsStorySpeakerSummary = httpsCallable(functions, 'jobsStorySpeakerSummary', { timeout: 540000 });
                    const summaryResult = await callAPIWithRetry( p => jobsStorySpeakerSummary(p), {
                        formData, storyData: speakerResult.data, producerData: producedResultLocal,
                        storyIntensity: storyTone.storyIntensity, storyLength: storyTone.storyLength, tone: storyTone.story,
                    });
                    storySpeakerSummaryFeeder = summaryResult.data;
                    setStorySpeakerSummaryResult(storySpeakerSummaryFeeder);
                    setApiCalls(prev => ({ ...prev, storySpeakerSummary: false }));
                    setApiCallsCompleted(prev => ({ ...prev, storySpeakerSummary: true }));
                    setSectionTimes(prev => ({ ...prev, storySpeakerSummary: Date.now() - storySpeakerSummaryStartTime }));
                    playSound();
                } catch (e) { console.error("SS error:", e); setApiCalls(prev=>({...prev,storySpeaker:false,storySpeakerSummary:false})); setError(`TT gen failed: ${e.message}`); }
            }

            try {
                setCurrentStep("Transformation Analysis");
                setApiCalls(prev => ({ ...prev, businessTransformation: true }));
                const jobsBusinessTransformationFromTo = httpsCallable(functions, 'jobsBusinessTransformationFromTo', { timeout: 540000 });
                const transformationResult = await callAPIWithRetry( p => jobsBusinessTransformationFromTo(p), {
                    formData, storyData: storyResultLocal, businessAnalysis: businessAnalysisDataFeeder, tone: storyTone.story,
                });
                businessTransformationFeeder = transformationResult.data;
                setBusinessTransformationResult(businessTransformationFeeder);
                const jobsBusinessCostOfInaction = httpsCallable(functions, 'jobsBusinessCostOfInaction', { timeout: 540000 });
                const costOfInactionResult = await callAPIWithRetry( p => jobsBusinessCostOfInaction(p), {
                    formData, storyData: storyResultLocal, businessAnalysis: businessAnalysisDataFeeder, tone: storyTone.story,
                });
                businessCostOfInactionFeeder = costOfInactionResult.data;
                setBusinessCostOfInactionResult(businessCostOfInactionFeeder);
                setApiCallsCompleted(prev => ({ ...prev, businessTransformation: true }));
                setApiCalls(prev => ({ ...prev, businessTransformation: false }));
                playSound();
            } catch (e) { console.error("BT/COI error:", e); setApiCalls(prev=>({...prev,businessTransformation:false})); setError(`T/COI failed: ${e.message}`); }

            try {
                setCurrentStep("Sales Analysis");
                const salesAnalysisCall = httpsCallable(functions, 'jobsSalesAnalysis', { timeout: 540000 });
                const salesAnalysisResult = await callAPIWithRetry( p => salesAnalysisCall(p), {
                    ...formData, storyChapters: storyChaptersDataFeeder,
                    podcastInsights: researchDataFeeder?.podcastInsights ? JSON.stringify(researchDataFeeder.podcastInsights) : JSON.stringify({}),
                    producedResult: producedResultLocal, personaResult: personaDataFeeder, researchData: researchDataFeeder,
                    businessNewsData: businessNewsFeeder, industryTrendsData: industryTrendsFeeder,
                    caseStudiesData: caseStudiesDataFeeder||{},
                    competitiveAnalysisResult: competitiveAnalysisResultFeeder
                });
                salesAnalysisDataFeeder = salesAnalysisResult.data;
                setSalesAnalysisData(salesAnalysisDataFeeder);
                setApiCalls(prev => ({ ...prev, salesAnalysis: false }));
                setSectionTimes(prev => ({ ...prev, salesAnalysis: Date.now() - salesAnalysisStartTime }));
                setApiCallsCompleted(prev => ({ ...prev, salesAnalysis: true }));

                // Ensure background tasks (Competitive Analysis, Case Studies) are complete before saving to Firestore
                try {
                    //console.log("Waiting for background tasks (Competitive Analysis, Case Studies) to complete...");
                    await Promise.all([competitiveAnalysisPromise, caseStudiesPromise]);
                    //console.log("Background tasks completed. competitiveAnalysisResultFeeder:", competitiveAnalysisResultFeeder, "caseStudiesDataFeeder:", caseStudiesDataFeeder);
                    // Feeder variables (competitiveAnalysisResultFeeder, caseStudiesDataFeeder) 
                    // should have been populated by their respective IIFEs.
                } catch (bgError) {
                    console.error("Error awaiting background tasks (Competitive Analysis/Case Studies):", bgError);
                    // Decide if this error should halt the process or be logged.
                    // setError(`Background task failed: ${bgError.message}`); // Optionally inform user
                    // If these are critical, this might need to throw or handle more explicitly.
                }
                
                try {

                    // console.log("*** --------------------------------")
                    // console.log("maxDeepDataFeeder", maxDeepDataFeeder)
                    // console.log("*** --------------------------------")

                    const storiesRef = collection(db, "stories");
                    const docRef = await addDoc(storiesRef, {
                        type: "tell", userDisplayName: user?.displayName || "", userEmail: user?.email || "",
                        userPhotoURL: user?.photoURL || "", visibility: "private", previewThumb: "blank",
                        intelligence: {
                            scenario: { promptCustomerName: formData.promptCustomerName, promptCustomerProblem: formData.promptCustomerProblem,
                                        promptCustomerRole: formData.promptCustomerRole, promptProductName: formData.promptProductName,
                                        promptCompanyName: formData.promptCompanyName },
                            researchDeepData: researchDeepDataFeeder, businessModelData: businessModelDataFeeder,
                            eli5DataBusiness: eli5DataBusinessFeeder, eli5DataIndustry: eli5DataIndustryFeeder,
                            producedResult: producedResultLocal, eli5BusinessValue: eli5BusinessValueDataLocal,
                            storyChapters: storyChaptersDataResult, businessAnalysis: businessAnalysisDataFeeder,
                            salesAnalysis: salesAnalysisDataFeeder, storySpeaker: storySpeakerSummaryFeeder,
                            storySpeakerSummary: storySpeakerSummaryFeeder, businessTransformation: businessTransformationFeeder,
                            businessCostOfInaction: businessCostOfInactionFeeder, producedWrapper: producedResultLocal || {},
                            personaData: personaDataFeeder || [], researchData: researchDataFeeder || {},
                            maxDeepData: maxDeepDataFeeder, executiveInterview: researchDataFeeder?.podcastInsights || [],
                            businessNewsData: businessNewsFeeder || {}, industryTrendsData: industryTrendsFeeder || {},
                            tone: storyTone?.story || "neutral", storyIntensity: storyTone?.storyIntensity || "medium",
                            customerInsights: researchDataFeeder?.customerInsights || [], productInsights: researchDataFeeder?.productInsights || [],
                            podcastInsights: researchDataFeeder?.podcastInsights || [],
                            caseStudiesData: caseStudiesDataFeeder||{},
                            competitiveAnalysisResult: competitiveAnalysisResultFeeder
                        },
                        lastModified: serverTimestamp(),
                        role: { writer: [user?.uid||""], reader: [user?.uid||""], owner: [user?.uid||""], commenter: [user?.uid||""] },
                        layoutType: "linear", layoutCircleDistance: 70, backgroundModel: "background_plain",
                        backgroundHeight: -5, backgroundSky: "skyBackground1", backgroundMatcap: "C8C8C8_3F3F3F_787878_5C5C5C",
                        navigation: "nav1", imageStoryBackground: "/media/icon/B3-Image-Placeholder.png",
                        imageStoryLogo: "/media/icon/B3-Image-Placeholder.png", analyticsViewTotal: 0,
                        analyticsLastViewed: serverTimestamp(), cameraOrbitOn: 1, messageUserId: user?.uid || "",
                        uiStyle: { "nav-region-bg": "#111111", "nav-progress-meter-bg": "#009ce0", "super-subtitle-color": "#cccccc",
                                   "nav-button-text-on": "#009ce0", "nav-button-bg-off": "#b3b3b3", "super-list-text-color": "#000000",
                                   "super-list-icon-color": "#000000", "nav-button-bg-on": "#222222", "super-main-title-color": "#009ce0",
                                   "super-main-bg-color": "#111111", "super-secondary-bg-color": "#000000", "nav-button-text-off": "#4d4d4d",
                                   "super-highlight-color": "#009ce0", "nav-progress-meter": "#808080" }, 
                        createdAt: serverTimestamp()
                    });
                    setModalData({
                        researchData: researchDataFeeder, businessModelData: businessModelDataFeeder,
                        researchDeepData: researchDeepDataFeeder, businessNewsData: businessNewsFeeder,
                        industryTrendsData: industryTrendsFeeder, eli5DataBusiness: eli5DataBusinessFeeder,
                        eli5DataIndustry: eli5DataIndustryFeeder, maxDeepData: maxDeepDataFeeder,
                        businessAnalysis: businessAnalysisDataFeeder, eli5BusinessValue: eli5BusinessValueDataLocal,
                        competitiveAnalysisResult: competitiveAnalysisResultFeeder, caseStudiesData: caseStudiesDataFeeder
                    });
                    const docSnap = await getDoc(doc(db, "stories", docRef.id));
                    if (docSnap.exists()) setIntelligenceFoundation(docSnap.data().intelligence);
                    else console.log("No doc after save!");
                } catch (e) { console.error("FS save error:", e); }
                playSound();
            } catch (e) { console.error("SA error:", e); setApiCalls(prev=>({...prev,salesAnalysis:false})); setError(`SA failed: ${e.message}`); }

        } catch (error) {
            console.error("Parallel post-story error:", error);
            // setError already called by individual steps if they throw
            // No specific setError here unless it's for the orchestration itself
        }
    } catch (error) { // Catch for the main try block
        console.error("Overall generation process error in service:", error);
        // setError might have been called by a specific step that threw
        // If not, or to add a general message:
        // params.setError(`Overall story generation failed: ${error.message}`);
    } finally { // Finally for the main try block
        setCurrentStep("Finished");
        setOpenPaths(true);
        setIsTimerRunning(false);
        setLoading(false);
        setShowProgressBar(true); // Or false, based on desired final state
        playSound();
    }
}; 